/**
 * CaptureSession — continuous camera session for front → side → optional rotation.
 *
 * Keeps a single MediaStream and RealtimePose instance alive across all phases.
 * Voice guide introduces the flow upfront and coaches the user through each step.
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import { useAppStore } from '@/lib/store';
import { PoseOverlay } from './PoseOverlay';
import { RealtimePose } from '@/lib/realtime-pose';
import { AngleDetector, TARGET_ANGLES } from '@/lib/angle-detector';
import { captureKeyframe, MIN_KEYFRAMES } from '@/lib/rotation-processor';
import { useBeep } from '@/lib/use-beep';
import { useVoiceGuide } from '@/lib/use-voice-guide';
import type { PoseFrame } from '@/lib/realtime-pose';
import type { RotationKeyframe } from '@/types/measurements';

type SessionPhase =
  | 'intro'
  | 'front'
  | 'front_preview'
  | 'side'
  | 'side_preview'
  | 'precision_prompt'
  | 'rotating'
  | 'complete';

/** Seconds of stability required before auto-capture countdown starts. */
const STABILITY_REQUIRED = 1.5;
/** Countdown duration (3-2-1). */
const COUNTDOWN_SECONDS = 3;
/** Degrees per second threshold for "slow down" warning. */
const MAX_ROTATION_SPEED = 60;
/** Seconds stalled before "keep turning" prompt. */
const STALL_THRESHOLD_SEC = 3;

const FINAL_BEEP = { freq: 1200, ms: 300 } as const;

export function CaptureSession() {
  const {
    setFrontPhoto,
    setSidePhoto,
    addRotationKeyframe,
    clearRotationKeyframes,
    setCaptureMode,
    setStep,
  } = useAppStore();

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const poseRef = useRef<RealtimePose | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCapturedRef = useRef(false);

  // Rotation-specific refs
  const angleDetectorRef = useRef<AngleDetector | null>(null);
  const lastAngleRef = useRef<{ angle: number; time: number } | null>(null);
  const lastCaptureTimeRef = useRef<number>(0);

  const [phase, setPhase] = useState<SessionPhase>('intro');
  const [cameraReady, setCameraReady] = useState(false);
  const [poseFrame, setPoseFrame] = useState<PoseFrame | null>(null);
  const [modelLoading, setModelLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [videoNativeSize, setVideoNativeSize] = useState({ w: 0, h: 0 });

  // Photo previews (stored locally until session complete)
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [sidePreview, setSidePreview] = useState<string | null>(null);

  // Rotation state
  const [currentAngle, setCurrentAngle] = useState(0);
  const [capturedAngles, setCapturedAngles] = useState<Set<number>>(new Set());
  const [keyframes, setKeyframes] = useState<RotationKeyframe[]>([]);

  const { playBeep, dispose: disposeBeep } = useBeep();
  const voiceGuide = useVoiceGuide();

  // Clean up on unmount
  useEffect(() => {
    return () => {
      poseRef.current?.dispose();
      poseRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      disposeBeep();
      voiceGuide.dispose();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Voice alignment feedback for front/side phases ──
  useEffect(() => {
    if (!poseFrame || (phase !== 'front' && phase !== 'side')) return;
    if (countdown > 0) return;

    const { alignment } = poseFrame;
    const captureType = phase === 'side' ? 'side' : 'front';

    if (alignment.distance === 'too_close') {
      voiceGuide.speak('Just step back a bit for me!');
    } else if (alignment.distance === 'too_far') {
      voiceGuide.speak('Come a little closer, you\'re doing great!');
    } else if (!alignment.head) {
      voiceGuide.speak('I need to see your whole self! Show me that head!');
    } else if (!alignment.legs) {
      voiceGuide.speak('Almost there! Let me see those legs too!');
    } else if (!alignment.shoulders || !alignment.torso) {
      voiceGuide.speak(
        captureType === 'side'
          ? 'Turn a bit more sideways for me!'
          : 'Face me straight on, looking good!'
      );
    } else if (alignment.allAligned && poseFrame.stableSeconds >= 0.5 && poseFrame.stableSeconds < 1.5) {
      voiceGuide.speak(
        captureType === 'side' ? 'Beautiful profile! Hold it right there!' : 'Perfect! Hold that pose, champion!'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseFrame, phase, countdown]);

  // ── Auto-capture countdown for front/side ──
  useEffect(() => {
    if (phase !== 'front' && phase !== 'side') return;
    if (!poseFrame || !cameraReady) {
      if (countdown > 0) {
        setCountdown(0);
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      }
      return;
    }

    const { stableSeconds } = poseFrame;

    if (stableSeconds >= STABILITY_REQUIRED && countdown === 0 && !autoCapturedRef.current) {
      setCountdown(COUNTDOWN_SECONDS);
      voiceGuide.speak(String(COUNTDOWN_SECONDS), 'capture');

      let tick = COUNTDOWN_SECONDS;
      countdownTimerRef.current = setInterval(() => {
        tick -= 1;
        if (tick <= 0) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          setCountdown(0);
          autoCapturedRef.current = true;
          doCapture();
        } else {
          setCountdown(tick);
          voiceGuide.speak(String(tick), 'capture');
        }
      }, 1000);
    }

    if (stableSeconds < 0.5 && countdown > 0) {
      setCountdown(0);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseFrame?.stableSeconds, phase, cameraReady]);

  // ── Rotation: angle tracking & keyframe capture ──
  useEffect(() => {
    if (phase !== 'rotating' || !poseFrame || !angleDetectorRef.current) return;

    const estimate = angleDetectorRef.current.estimate(poseFrame.landmarks);
    if (!estimate) return;

    setCurrentAngle(estimate.angle);

    const now = Date.now();
    if (lastAngleRef.current) {
      const dt = (now - lastAngleRef.current.time) / 1000;
      if (dt > 0.2) {
        const angleDelta = Math.abs(estimate.angle - lastAngleRef.current.angle);
        const wrappedDelta = Math.min(angleDelta, 360 - angleDelta);
        const speed = wrappedDelta / dt;
        if (speed > MAX_ROTATION_SPEED) {
          voiceGuide.speak('Easy now, slow it down!');
        }
      }

      const timeSinceCapture = (now - lastCaptureTimeRef.current) / 1000;
      if (lastCaptureTimeRef.current > 0 && timeSinceCapture > STALL_THRESHOLD_SEC && capturedAngles.size < TARGET_ANGLES.length - 1) {
        const remaining = TARGET_ANGLES.length - capturedAngles.size;
        voiceGuide.speak(`You've got ${capturedAngles.size} angles, ${remaining} to go. Keep turning!`);
        lastCaptureTimeRef.current = now;
      }
    }
    lastAngleRef.current = { angle: estimate.angle, time: now };

    for (const target of TARGET_ANGLES) {
      if (capturedAngles.has(target)) continue;
      if (angleDetectorRef.current.isTargetReached(target)) {
        const video = videoRef.current;
        if (!video) break;

        const kf = captureKeyframe(video, target, poseFrame.landmarks);
        addRotationKeyframe(kf);
        setKeyframes((prev) => [...prev, kf]);
        setCapturedAngles((prev) => new Set([...prev, target]));
        lastCaptureTimeRef.current = Date.now();
        playBeep(1000, 80);

        const captured = capturedAngles.size + 1;
        const remaining = TARGET_ANGLES.length - captured;
        if (remaining <= 0) {
          voiceGuide.speak('Full rotation! You nailed it, champion!');
        } else if (captured === 1) {
          voiceGuide.speak(`First angle down! ${remaining} to go, keep spinning!`);
        } else if (captured === Math.floor(TARGET_ANGLES.length / 2)) {
          voiceGuide.speak(`Halfway! ${remaining} to go, you're killing it!`);
        } else if (remaining <= 2) {
          voiceGuide.speak(`Almost there! Just ${remaining} more!`);
        } else if (captured % 3 === 0) {
          voiceGuide.speak(`That's ${captured} captured, ${remaining} to go!`);
        }
        break;
      }
    }

    if (capturedAngles.size >= TARGET_ANGLES.length - 1) {
      handleRotationComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poseFrame, phase]);

  // ── Camera management ──

  const stopCamera = useCallback(() => {
    poseRef.current?.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setVideoNativeSize({ w: 0, h: 0 });
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1080 },
          height: { ideal: 1920 },
        },
        audio: false,
      });

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        setCameraError('Camera element not ready. Please try again.');
        return;
      }

      video.srcObject = stream;
      await new Promise<void>((resolve) => {
        if (video.readyState >= 1) { resolve(); return; }
        video.addEventListener('loadedmetadata', () => resolve(), { once: true });
      });
      await video.play();
      setVideoNativeSize({ w: video.videoWidth, h: video.videoHeight });
      setCameraReady(true);

      // Fullscreen
      try {
        await containerRef.current?.requestFullscreen?.();
      } catch { /* Fine */ }

      // Start pose detection
      if (!poseRef.current) {
        poseRef.current = new RealtimePose((frame) => setPoseFrame(frame), 'front');
      }
      if (!poseRef.current.isReady && !poseRef.current.isLoading) {
        setModelLoading(true);
        await poseRef.current.load();
        setModelLoading(false);
      }
      poseRef.current.start(video);
    } catch {
      setCameraError('Could not start camera. Please check permissions.');
    }
  }, []);

  // ── Capture logic ──

  function doCapture() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w === 0 || h === 0) return;

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

    playBeep(FINAL_BEEP.freq, FINAL_BEEP.ms);
    voiceGuide.speak('Got it! Looking sharp!', 'capture');

    // Stop pose detection during preview (camera stream stays alive)
    poseRef.current?.stop();
    setPoseFrame(null);

    if (phase === 'front') {
      setFrontPreview(dataUrl);
      setFrontPhoto(dataUrl);
      setPhase('front_preview');
    } else if (phase === 'side') {
      setSidePreview(dataUrl);
      setSidePhoto(dataUrl);
      setPhase('side_preview');
    }
  }

  function handleManualCapture() {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
    doCapture();
  }

  // ── Phase transitions ──

  function handleStartSession() {
    voiceGuide.speakSequence([
      'Let\'s get your measurements!',
      'We\'ll do three quick steps: front photo, side photo, and an optional precision scan.',
      'First up, face the camera and stand with your full body visible!',
    ], 600);
    // Set phase first so the front UI renders as soon as camera is ready
    setPhase('front');
    startCamera();
  }

  function handleFrontAccepted() {
    voiceGuide.speak('Great! Now let\'s get your side view. Just turn sideways naturally, arms relaxed!', 'capture');
    autoCapturedRef.current = false;
    setCountdown(0);
    // Switch pose detection to side mode
    poseRef.current?.setCaptureType('side');
    poseRef.current?.start(videoRef.current!);
    setPhase('side');
  }

  function handleRetakeFront() {
    setFrontPreview(null);
    autoCapturedRef.current = false;
    setCountdown(0);
    poseRef.current?.setCaptureType('front');
    poseRef.current?.start(videoRef.current!);
    setPhase('front');
  }

  function handleSideAccepted() {
    // Stop camera and go to precision prompt
    poseRef.current?.stop();
    setPoseFrame(null);
    setPhase('precision_prompt');
    voiceGuide.speak('Awesome! Want to do a precision scan for the best accuracy?', 'capture');
  }

  function handleRetakeSide() {
    setSidePreview(null);
    autoCapturedRef.current = false;
    setCountdown(0);
    poseRef.current?.setCaptureType('side');
    poseRef.current?.start(videoRef.current!);
    setPhase('side');
  }

  function handleStartRotation() {
    setCaptureMode('rotation');
    // Initialize angle detector
    const storedFront = useAppStore.getState().frontDetection;
    if (storedFront?.landmarks) {
      const lS = storedFront.landmarks[11];
      const rS = storedFront.landmarks[12];
      if (lS && rS) {
        angleDetectorRef.current = new AngleDetector(Math.abs(rS.x - lS.x));
      }
    }
    if (!angleDetectorRef.current) {
      angleDetectorRef.current = new AngleDetector();
    }

    clearRotationKeyframes();
    setCapturedAngles(new Set());
    setKeyframes([]);
    lastAngleRef.current = null;
    lastCaptureTimeRef.current = Date.now();

    // Resume pose detection for rotation
    poseRef.current?.setCaptureType('front');
    poseRef.current?.start(videoRef.current!);
    setPhase('rotating');
    voiceGuide.speak('Now turn slowly like a supermodel! Nice and easy!', 'capture');
  }

  function handleSkipRotation() {
    setCaptureMode('side');
    finishSession();
  }

  function handleRotationComplete() {
    setCaptureMode('rotation');
    playBeep(1200, 300);
    voiceGuide.speak('Awesome! Let me crunch those numbers for you!');
    poseRef.current?.stop();
    setPoseFrame(null);
    finishSession();
  }

  function handleFinishRotationEarly() {
    if (keyframes.length >= MIN_KEYFRAMES) {
      handleRotationComplete();
    }
  }

  function finishSession() {
    stopCamera();
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setPhase('complete');
    // Navigate to photo review
    setTimeout(() => {
      setStep('photo_review');
    }, 800);
  }

  function handleCancel() {
    stopCamera();
    clearRotationKeyframes();
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setStep('reference');
  }

  // Always render video + canvas so refs are available when startCamera() runs.
  // Show intro as overlay when camera hasn't started yet.
  return (
    <div ref={containerRef} className={cameraReady ? 'camera-container' : 'relative min-h-screen'}>
      {/* Video + canvas — always in DOM */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cameraReady ? 'camera-feed' : 'hidden'}
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* ── Intro screen (overlay before camera starts) ── */}
      {!cameraReady && phase === 'intro' && (
        <div className="screen items-center justify-center text-center">
          <h2 className="text-2xl font-bold text-gray-900">Photo Session</h2>
          <p className="mt-2 text-sm text-gray-500">
            We&apos;ll capture your body from multiple angles in one quick session.
            The camera stays on the whole time.
          </p>

          <div className="mt-6 space-y-2">
            <div className="card text-left">
              <h3 className="text-sm font-semibold text-gray-900">3-step plan:</h3>
              <ul className="mt-2 space-y-1 text-sm text-gray-500">
                <li>1. Front photo — face the camera, full body</li>
                <li>2. Side photo — turn sideways naturally</li>
                <li>3. Precision scan — slow 360° turn (optional)</li>
              </ul>
            </div>
          </div>

          {cameraError && (
            <p className="mt-4 text-sm text-red-500">{cameraError}</p>
          )}

          <div className="mt-6 w-full space-y-3">
            <button onClick={handleStartSession} className="btn-primary">
              Start Session
            </button>
            <button onClick={() => setStep('reference')} className="btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* ── Front/Side capture phase ── */}
      {(phase === 'front' || phase === 'side') && (
        <>
          {/* Pose overlay */}
          <PoseOverlay
            frame={poseFrame}
            videoWidth={videoNativeSize.w}
            videoHeight={videoNativeSize.h}
            modelLoading={modelLoading}
            countdown={countdown}
            selfTimerCountdown={0}
            voiceGuideActive={true}
            captureType={phase === 'side' ? 'side' : 'front'}
          />

          {/* Top hint */}
          {countdown === 0 && (
            <div
              className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/60 to-transparent px-4 pb-8 pt-4"
              style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
            >
              <p className="text-center text-xs font-medium text-white/70">
                Step {phase === 'front' ? '1' : '2'} of 3
              </p>
              <p className="mt-1 text-center text-sm font-medium text-white/90">
                {poseFrame?.alignment.allAligned
                  ? 'Hold still — auto-capturing...'
                  : phase === 'side'
                    ? 'Stand sideways with your full body visible'
                    : 'Stand facing the camera, full body visible'}
              </p>
            </div>
          )}

          {/* Bottom controls */}
          {countdown === 0 && (
            <div className="camera-controls">
              <div className="flex w-full items-center justify-center gap-4">
                {/* Manual capture */}
                <button
                  onClick={handleManualCapture}
                  className="rounded-full bg-white p-1.5 shadow-lg active:scale-90 transition-transform"
                  aria-label="Take photo"
                >
                  <div className="h-[60px] w-[60px] rounded-full border-[5px] border-brand-500" />
                </button>

                {/* Cancel */}
                <button
                  onClick={handleCancel}
                  className="rounded-full bg-black/50 p-3 text-white backdrop-blur-sm"
                  aria-label="Cancel"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Front preview ── */}
      {phase === 'front_preview' && frontPreview && (
        <>
          <img
            src={frontPreview}
            alt="Front photo"
            className="absolute inset-0 h-full w-full object-contain"
            style={{ zIndex: 2 }}
          />
          <div
            className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/70 to-transparent px-4 pb-10 pt-4"
            style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
          >
            <p className="text-center text-base font-semibold text-white">
              Front photo captured!
            </p>
            <p className="mt-1 text-center text-sm text-white/70">
              Looking good? Next up: side photo.
            </p>
          </div>
          <div className="camera-controls">
            <div className="mx-auto flex w-full max-w-sm gap-3 px-4">
              <button
                onClick={handleRetakeFront}
                className="flex-1 rounded-xl border-2 border-white/40 px-4 py-3.5 text-base font-semibold text-white active:scale-[0.97] transition-transform"
              >
                Retake
              </button>
              <button
                onClick={handleFrontAccepted}
                className="flex-1 rounded-xl bg-brand-500 px-4 py-3.5 text-base font-semibold text-white active:scale-[0.97] transition-transform"
              >
                Next: Side
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Side preview ── */}
      {phase === 'side_preview' && sidePreview && (
        <>
          <img
            src={sidePreview}
            alt="Side photo"
            className="absolute inset-0 h-full w-full object-contain"
            style={{ zIndex: 2 }}
          />
          <div
            className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/70 to-transparent px-4 pb-10 pt-4"
            style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
          >
            <p className="text-center text-base font-semibold text-white">
              Side photo captured!
            </p>
            <p className="mt-1 text-center text-sm text-white/70">
              Great work! Want to do a precision scan?
            </p>
          </div>
          <div className="camera-controls">
            <div className="mx-auto flex w-full max-w-sm gap-3 px-4">
              <button
                onClick={handleRetakeSide}
                className="flex-1 rounded-xl border-2 border-white/40 px-4 py-3.5 text-base font-semibold text-white active:scale-[0.97] transition-transform"
              >
                Retake
              </button>
              <button
                onClick={handleSideAccepted}
                className="flex-1 rounded-xl bg-brand-500 px-4 py-3.5 text-base font-semibold text-white active:scale-[0.97] transition-transform"
              >
                Continue
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Precision prompt (after side accepted) ── */}
      {phase === 'precision_prompt' && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/80">
          <div className="mx-4 max-w-sm rounded-2xl bg-white p-6 text-center">
            <h3 className="text-lg font-bold text-gray-900">Precision Scan?</h3>
            <p className="mt-2 text-sm text-gray-500">
              A slow 360° rotation gives the most accurate circumference measurements.
              Prop your phone on a surface and rotate.
            </p>
            <div className="mt-6 space-y-3">
              <button
                onClick={handleStartRotation}
                className="btn-primary"
              >
                Yes, do precision scan
              </button>
              <button
                onClick={handleSkipRotation}
                className="btn-secondary"
              >
                Skip — use front + side only
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rotation phase ── */}
      {phase === 'rotating' && (
        <>
          {/* Rotation progress */}
          <div
            className="absolute left-0 right-0 top-0 z-[3] bg-gradient-to-b from-black/60 to-transparent px-4 pb-8 pt-4"
            style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
          >
            <p className="text-center text-xs font-medium text-white/70">
              Step 3 of 3 — Precision Scan
            </p>
            <p className="mt-1 text-center text-sm font-medium text-white/90">
              Turn slowly... {keyframes.length}/{TARGET_ANGLES.length} angles captured
            </p>
          </div>

          {/* Angle indicator ring */}
          <div className="absolute inset-0 z-[2] flex items-center justify-center pointer-events-none">
            <svg viewBox="0 0 200 200" className="h-48 w-48 opacity-60">
              <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
              {TARGET_ANGLES.map((angle) => {
                const rad = ((angle - 90) * Math.PI) / 180;
                const x = 100 + 90 * Math.cos(rad);
                const y = 100 + 90 * Math.sin(rad);
                const captured = capturedAngles.has(angle);
                return (
                  <circle
                    key={angle}
                    cx={x}
                    cy={y}
                    r={captured ? 6 : 4}
                    fill={captured ? '#22c55e' : 'rgba(255,255,255,0.4)'}
                  />
                );
              })}
              {/* Current angle indicator */}
              {(() => {
                const rad = ((currentAngle - 90) * Math.PI) / 180;
                const x = 100 + 80 * Math.cos(rad);
                const y = 100 + 80 * Math.sin(rad);
                return <circle cx={x} cy={y} r={8} fill="#f59e0b" />;
              })()}
            </svg>
          </div>

          {/* Bottom controls */}
          <div className="camera-controls">
            <div className="flex w-full flex-col items-center gap-3">
              {/* Primary: Finish / Process button — always visible */}
              <button
                onClick={handleFinishRotationEarly}
                disabled={keyframes.length < MIN_KEYFRAMES}
                className={`rounded-full px-8 py-4 text-base font-bold shadow-lg transition-all ${
                  keyframes.length >= MIN_KEYFRAMES
                    ? 'bg-green-500 text-white active:scale-95 animate-pulse'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
              >
                {keyframes.length >= MIN_KEYFRAMES
                  ? `Process with ${keyframes.length} angles`
                  : `Need ${MIN_KEYFRAMES - keyframes.length} more angles`}
              </button>
              <button
                onClick={handleCancel}
                className="rounded-full bg-red-500/80 px-5 py-2 text-sm font-semibold text-white backdrop-blur-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Complete (briefly shown before navigation) ── */}
      {phase === 'complete' && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/80">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-green-500/90 flex items-center justify-center">
              <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-4 text-lg font-semibold text-white">All done!</p>
          </div>
        </div>
      )}

      {cameraError && (
        <div className="absolute bottom-28 left-4 right-4 z-[4]">
          <p className="rounded-lg bg-red-500/90 px-4 py-2 text-center text-sm text-white">
            {cameraError}
          </p>
        </div>
      )}
    </div>
  );
}
